interface API_Params_Obj {
  [key: string]: string;
}

const DEFAULT_REPORT_PARSE_TYPE = "html";

export class API_ROUTES {
  static generateQuery(params: API_Params_Obj) {
    if (!params) return "";

    return Object.keys(params).reduce((acc, key, index) => {
      if (!params[key]) return acc;

      if (index) acc += "&";

      if (params[key]) acc += `${key}=${params[key]}`;

      return acc;
    }, "?");
  }

  static generateQueryWithEmptyValues(params: API_Params_Obj) {
    if (!params) return "";

    return Object.keys(params).reduce((acc, key) => {
      const value = params[key];

      if (value === undefined || value === null || value === "") return acc;

      if (acc !== "?") acc += "&";
      acc += `${key}=${value}`;

      return acc;
    }, "?");
  }

  /** @method get */
  static readonly STATUS = "status";

  /** @method post */
  static readonly ACCOUNT_ACTIVATION = "/auth/activate";

  /** @method post @method put */
  static readonly PASSWORD_RESET = "/auth/password/reset";

  /** @method get */
  static readonly COMPANY_DASHBOARD = "/company/dashboard";

  /** @method get */
  static readonly EMPLOYEE_DASHBOARD = "/employee/dashboard";

  /** @method get */
  static readonly EMPLOYEE_NAME = "/employee/name";

  /** @method post */
  static readonly SEND_NPS_SCORE = "/nps";

  /** @method get */
  static EMPLOYEE_SUMMARY(id: number | string) {
    return `/company/employees/employee/${id}/summary`;
  }

  /** @method get */
  static EMPLOYEE_NAME_BY_ID(id: number | string) {
    return `/company/employees/employee/${id}/name`;
  }

  /** @method post */
  static EMPLOYEE_LEAVE(user_id: number | string) {
    return `/company/employees/employee/${user_id}/leave`;
  }

  /** @method post */
  static EXPERT_COMPANY_EMPLOYEE_LEAVE(company_id: number, user_id: number | string) {
    return `/expert/companies/${company_id}/employees/${user_id}/leave`;
  }

  /** @method get */
  static readonly COMPANY_REPORT_CHANGE = "/company/report_change";

  /** @method get */
  static readonly COMPANY_DOCUMENTS = "/company/documents";

  /** @method get */
  static readonly COMPANY_EMPLOYEES = "/company/employees";

  /** @method get */
  static readonly COMPANY_EMPLOYEE_ONBOARD_EMPLOYEE = "/company/employees/onboard_employee";

  // Documents V2
  /** @method get */
  static DOCUMENTS_DIRECTORY(object_id: number, object_type: string) {
    return `/documents/directory?object_id=${object_id}&object_type=${object_type}`;
  }

  /** @method get @method delete */
  static DOCUMENTS_FILE(fileId: number) {
    return `/documents/files/${fileId}`;
  }

  /** @method post */
  static readonly DOCUMENTS_UPLOAD = "/documents";

  /** @method post */
  static readonly DOCUMENTS_FILES_COMPRESSION = "/documents/files/compression";

  /** @method post  */
  static DOCUMENTS_UPLOAD_TO_DIRECTORY(directory_id: number) {
    return `/documents/directory/${directory_id}`;
  }

  /** @method get  */
  static DOCUMENTS_DIRECTORY_FILES(directory_id: number) {
    return `/documents/directory/${directory_id}/files`;
  }

  /** @method get  */
  static DOCUMENTS_DIRECTORY_DOWNLOAD_ALL(directory_id: number) {
    return `/documents/directory/${directory_id}/zip`;
  }

  /** @method post */
  static readonly DOCUMENTS_PARSE = "/documents/parse";

  /** @method put  */
  static DOCUMENTS_DIRECTORY_DECISION(
    directory_id: number,
    decision: "approve" | "reject" | "withdraw"
  ) {
    return `/documents/directory/${directory_id}/decision?value=${decision}`;
  }

  /** @method put  */
  static DOCUMENTS_FILES_MOVE_TO(file_id: number, directory_id: number) {
    return `/documents/files/${file_id}/move?directory_id=${directory_id}`;
  }

  /** @method get */
  static DOCUMENTS_EXAMPLE(id: number | string) {
    return `/documents/example?document_template_id=${id}`;
  }
  // End Documents V2

  /** @method get */
  static COMPANY_DOCUMENTS_EMPLOYEE(id: number | string) {
    return `/company/employees/employee/${id}/documents`;
  }

  /** @method get */
  static TASKS() {
    return "/tasks";
  }

  /** @method get */
  static TASKS_BY_FILTER() {
    return "/tasks/by_filter";
  }

  /** @method get @method delete */
  static TASK(id: number | string) {
    return `/tasks/${id}`;
  }

  /** @method get */
  static ARCHIVE_TASK(id: number | string) {
    return `/tasks/${id}/archive`;
  }

  /** @method get */
  static FINISH_TASK(id: number | string) {
    return `/tasks/${id}/finish`;
  }

  /** @method post */
  static TASKS_FINISH_ONBOARD_EMPLOYEE(id: number | string) {
    return `/tasks/${id}/finish/onboard_employee`;
  }

  /** @method get */
  static readonly DOCUMENTS = "/docs";

  /** @method get @method put */
  static DOCUMENT(id: number | string) {
    return `/docs/${id}`;
  }

  /** @method post */
  static DOCUMENT_COMPANY(id: number | string) {
    return `/docs/${id}/company`;
  }

  /** @method get @method post */
  static COS_DATE_DOCUMENT(id: number | string) {
    return `/docs/${id}/cos_date`;
  }

  /** @method post */
  static COS_FORM_DOCUMENT_CONFIRM(id: number | string) {
    return `/docs/${id}/confirm`;
  }

  /** @method get @method delete */
  static DOCUMENT_FILE(documentId: number | string, fileId: null | number) {
    return `/docs/${documentId}/file/${fileId}`;
  }

  /** @method put */
  static ARCHIVE_DOCUMENT_FILE(documentId: number | string, fileId: null | number) {
    return `/docs/${documentId}/file/${fileId}/archive`;
  }

  /** @method get */
  static DOCUMENT_EXAMPLE(id: number | string) {
    return `/docs/${id}/example`;
  }

  /** @method get */
  static readonly EMPLOYEE_COMPANY_INFO = "/employee/company/info";

  /** @method get */
  static USERFLOW_CURRENT_STEP(id: number | string, params?: object) {
    const url = `/flow/userflow/${id}/current_step`;

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method post */
  static USERFLOW_ANSWER(id: number | undefined, params?: object) {
    const url = `/flow/userflow/${id}/answer`;

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  static USERFLOW_GO_BACK(id: number | string, params?: object) {
    const url = `/flow/userflow/${id}/back`;

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  static USERFLOW_EMPLOYEES(id: number | string, params?: object) {
    const url = `/flow/userflow/${id}/employees`;

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  static readonly FLOW = "/flow";

  /** @method get */
  static readonly PARSED_FLOW = "/flow/parsed";

  /** @method get */
  static readonly COMPLIANCE_PROFILE = "/compliance/profile";

  /** @method get */
  static readonly COMPLIANCE_PROFILE_HISTORY = "/compliance/profile/history";

  /** @method get */
  static COMPLIANCE_PROFILE_EMPLOYEE(id: number | string) {
    return `/compliance/profile/employee/${id}`;
  }

  /** @method get */
  static COMPLIANCE_PROFILE_HISTORY_EMPLOYEE(id: number) {
    return `/compliance/profile/employee/${id}/history`;
  }

  /** @method post */
  static readonly COMPLIANCE_RTWC = "/compliance/rtwc";

  /** @method get */
  static COMPLIANCE_RTWC_DATA(employee_id: number) {
    return `/compliance/rtwc/data?employee_id=${employee_id}`;
  }

  /** @method get */
  static COMPLIANCE_RTWC_FINISH(task_id: number, result: "accept" | "ecs" | "decline") {
    return `/compliance/rtwc/finish?task_id=${task_id}&result=${result}`;
  }

  /** @method get */
  static COMPLIANCE_RTWC_VALIDATE(task_id: number) {
    return `/compliance/rtwc/validate?task_id=${task_id}`;
  }

  /** @method get */
  static EXPERT_COMPANY_DOCUMENTS_DOWNLOAD_ARCHIVE(company_id: number) {
    return `expert/companies/${company_id}/documents/download_archive`;
  }

  /** @method get */
  static EXPERT_COMPANY_EMPLOYEE_DOCUMENTS_DOWNLOAD_ARCHIVE(
    company_id: number,
    employee_id: number | string
  ) {
    return `expert/companies/${company_id}/employees/${employee_id}/documents/download_archive`;
  }

  /** @method get */
  static EXPERT_COMPLIANCE_COMPANY_DOCUMENT_STATUS(
    companyId: number | string,
    documentId: number | string,
    status: string
  ) {
    return `/expert/companies/${companyId}/documents/${documentId}/status?value=${status}`;
  }

  /** @method get */
  static EXPERT_COMPLIANCE_COMPANY_EMPLOYEE_STATUS(
    companyId: number | string,
    employeeId: number | string,
    documentId: number | string,
    status: string
  ) {
    return `expert/companies/${companyId}/employees/${employeeId}/documents/${documentId}/status?value=${status}`;
  }

  /** @method post */
  static FINISH_REPORT_EMPLOYEES_NUMBER_CHANGE(id: number | string) {
    return `/tasks/${id}/finish/report_change_employees`;
  }

  /** @method post */
  static readonly STEPS_VALIDATION = "/validate/steps";

  /** @method get */
  static readonly GUIDES = "/guide";

  /** @method get */
  static GUIDE(id: number | string) {
    return `/guide/${id}`;
  }

  /** @method get */
  static CASE(company?: boolean | null) {
    let url = `/case`;

    if (company) url += `?company=true`;

    return url;
  }

  static CASES(params?: object) {
    // const url = "/cases";
    const url = "employer/cases_by_filter";

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  // TO DO: delete and change to CASE once testing is done
  static CASE_EXPERT_CASES() {
    return `/case/expert_cases`;
  }

  /** @method get */
  static CASE_EXPERT_FILTERS() {
    return "/case/filter_list";
  }

  /** @method get */
  // candidate_email, candidate_first_name, candidate_last_name, role_template_id
  static CASE_CREATE(alias: string, params: object) {
    let url = `/case/create`;

    const query = this.generateQuery({ alias, ...params } as API_Params_Obj);

    if (query) url += query;

    return url;
  }

  /** @method get */
  // static CASE_CREATE(alias?: string, target_id?: string | number, case_from_id?: string | number) {
  //   let url = `/case/create`;
  //
  //   const params = {
  //     alias,
  //     target_id,
  //     case_from_id,
  //   };
  //
  //   const query = this.generateQuery(params as API_Params_Obj);
  //
  //   if (query) url += query;
  //
  //   return url;
  // }

  /** @method get */
  static CASE_CREATE_COS_SW(params: {
    alias?: string;
    case_from_id?: string | number;
    company_id?: string | number;
  }) {
    let url = `/case/create`;

    const query = this.generateQuery(params as API_Params_Obj);

    if (query) url += query;

    return url;
  }

  /** @method delete */
  static EXPERT_CASE_DELETE(caseId: number | string) {
    return `/case/${caseId}/expert/case`;
  }

  /** @method delete */
  static EXPERT_CASE_DELETE_PLUS_CANDIDATE_PROFILE(caseId: number | string) {
    return `/case/${caseId}/expert/user`;
  }

  /** @method delete */
  static EXPERT_CASE_DELETE_PLUS_EC(caseId: number | string) {
    return `/case/${caseId}/expert/case?ec_delete=`;
  }

  /** @method get */
  static CASE_ID(id: number | string, company?: boolean | null) {
    let url = `/case/${id}`;

    if (company) url += `?company=true`;

    return url;
  }

  /** @method get */
  static CASE_DASHBOARD(id: number | string, dashboardId: number | string) {
    return `/case/${id}/dashboard/${dashboardId}`;
  }

  /** @method get */

  static PROGRESS_PERCENT(caseId: number | string, case_dashboard_id: number | string) {
    return `/case/${caseId}/dashboard/${case_dashboard_id}/widget/progress`;
  }

  static CASE_PROFILE(id: number | string) {
    return `/case/${id}/profile`;
  }

  /** @method get */
  static CASE_PROFILE_ID(id: number | string, profileId: number | string) {
    return `/case/${id}/profile/${profileId}`;
  }

  /** @method get */
  static CASE_STEPS(id: number | string, dashboardId: number | string) {
    return `/case/${id}/dashboard/${dashboardId}/widget/steps`;
  }

  /** @method get @method post */
  static CASE_TASKS(caseId: number | string, status?: string | null) {
    let url = `/case/${caseId}/tasks`;

    if (status) url += `?status=${status}`;

    return url;
  }

  /** @method delete */
  static CASE_TASK(caseId: number | string, taskId: number | string) {
    return `/case/${caseId}/tasks/${taskId}`;
  }

  /** @method get */
  static CASE_SUMMARY(id: number | string, dashboardId: number | string) {
    return `/case/${id}/dashboard/${dashboardId}/widget/summary`;
  }

  /** @method get */
  static CASE_HIGHLIGHTS(id: number | string) {
    return `/case/${id}/expert/highlights`;
  }

  /** @method get */
  static POSTCODE(postcode: string) {
    return `/postcode/search?postcode=${encodeURIComponent(postcode)}`;
  }

  /** @method get */
  static CASE_ONLINE_APPLICATION(id: number | string, dashboardId: number | string) {
    return `/case/${id}/online_application/${dashboardId}`;
  }

  /** @method get */
  static CASE_DOCUMENTS_EXPERT(id: number | string) {
    return `/case/${id}/expert/documents`;
  }

  /** @method get @method post */
  static CASE_ACTIVITY_LOG(id: number | string) {
    return `/case/${id}/activity_log`;
  }

  /** @method put @method delete */
  static CASE_ACTIVITY_LOG_EDIT(id: number | string, log_id: number | string) {
    return `/case/${id}/activity_log/${log_id}`;
  }

  /** @method get @method post */
  static CASE_ACTIVITY_LOG_LIMIT(id: number | string, limit: number) {
    return `/case/${id}/activity_log?limit=${limit}`;
  }

  /** @method get */
  static CASE_EXPERT_DOCUMENT_TEMPLATE(caseId: number | string, caseTemplateId: number | string) {
    return `/case/${caseId}/expert/document_template?case_template_id=${caseTemplateId}`;
  }

  /** @method get */
  static CASE_EXPERT_CASE_PROGRESS_STEP(caseId: number | string, caseTemplateId: number | string) {
    return `/case/${caseId}/expert/case_progress_step?case_template_id=${caseTemplateId}`;
  }

  /** @method get */
  static CASE_MOVE_TO_STEP(caseId: number | string, stepId: number | string) {
    return `/case/${caseId}/steps/${stepId}/move`;
  }

  /** @method get */
  static CASE_EDIT_STEP(caseId: number | string, stepId: number | string) {
    return `/case/${caseId}/steps/${stepId}/edit`;
  }

  /** @method get */
  static CASE_EDIT_CHECK(caseId: number | string) {
    return `/case/${caseId}/edit_check`;
  }

  /** @method get */
  static CASE_EXPERT_DOCUMENT_STATUS(
    caseId: number | string,
    documentId: number | string,
    status: string
  ) {
    return `/case/${caseId}/expert/documents/${documentId}/decision?status=${status}`;
  }

  /** @method put */
  static CASE_EXPERT_HO_APPROVE(id: number | string) {
    return `/case/${id}/expert/ho_decision/approve`;
  }

  /** @method put */
  static CASE_EXPERT_HO_REJECT(id: number | string) {
    return `/case/${id}/expert/ho_decision/reject`;
  }

  /** @method put */
  static CASE_EXPERT_HO_RESET(id: number | string) {
    return `/case/${id}/expert/ho_decision/reset`;
  }

  /** @method get */
  static CASE_SEND_TO_REWIEW(id: number | string) {
    return `/case/${id}/send_to_review`;
  }

  /** @method get */
  static CASE_DOCUMENTS(case_id: number) {
    return `/case/${case_id}/documents`;
  }

  /** @method get @method put */
  static CASE_DOCUMENT(case_id: number, document_id: number | string) {
    return `/case/${case_id}/documents/${document_id}`;
  }

  /** @method get @method delete */
  static CASE_DOCUMENT_FILE(case_id: number, documentId: number | string, fileId: null | number) {
    return `case/${case_id}/documents/${documentId}/file/${fileId}`;
  }

  /** @method post */
  static CASE_DOCUMENT_FILES(case_id: number, documentId: number | string) {
    return `case/${case_id}/documents/${documentId}/file`;
  }

  /** @method get */
  static CASE_DOCUMENT_DOWNLOAD_ARCHIVE(case_id: number) {
    return `case/${case_id}/documents/download_archive`;
  }

  /** @method get */
  static CASE_DOCUMENT_SAVE_MULTIPLE(case_id: number, document_id: number | string) {
    return `case/${case_id}/documents/${document_id}/file/confirm_multiple`;
  }

  /** @method get */
  static COMPLIANCE_DOCUMENT_SAVE_MULTIPLE(document_id: number | string) {
    return `docs/${document_id}/confirm_multiple_files`;
  }

  /** @method put */
  static CASE_EXPERT_BOT(id: number | string, onlineApplicationId: number | string) {
    return `/case/${id}/online_application/${onlineApplicationId}/bot`;
  }

  // FORMBOT

  /** @method get */
  static CASE_FORMBOT_PLAN(id: number | string) {
    return `/case/${id}/formbot/plan`;
  }

  /** @method post */
  static CASE_FORMBOT_RUN(id: number | string) {
    return `/case/${id}/formbot/run`;
  }

  /** @method get */
  static CASE_ONLINE_APPLICATION_STRUCTURED_DATA(case_id: number | string, bot: "cos" | "inside") {
    return `/case/${case_id}/online_application/structured_data?bot=${bot}`;
  }

  /** @method get */
  static CASE_REPORT_PARSE(
    id: number | string,
    alias: string | undefined,
    type: string = DEFAULT_REPORT_PARSE_TYPE
  ) {
    const url = `case/${id}/report/parsed`;

    const params = {
      type,
      alias,
    };

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  static CASE_REPORT_CHANGE(id: number | string) {
    return `/case/${id}/report/change`;
  }

  /** @method get */
  static CASE_REPORT_GENERATE(id: number | string, alias: string | undefined) {
    let url = `/case/${id}/report/generate`;

    const params = {
      alias,
    };

    const query = this.generateQuery(params as API_Params_Obj);

    if (query) url += query;

    return url;
  }

  /** @method put */
  static CASE_REPORT_EDIT(id: number | string, alias?: string) {
    let url = `case/${id}/report/edit`;

    if (alias) url += `?alias=${alias}`;

    return url;
  }

  /** @method get */
  static GET_COVER_LETTER(id: string | number, role: string) {
    return `${role}/case/${id}/sponsorship/cover_letter`;
  }

  static GET_EXPERT_COVER_LETTER(id: string | number) {
    return `case/${id}/expert/cover_letter?html`;
  }

  /** @method get */
  static CASE_PROFILE_HISTORICAL(id: number) {
    return `/case/${id}/profile/historical`;
  }

  /** @method get */
  // TODO: Discuss outdated widgets and remove
  static SERVICE_REQUEST(service: string) {
    return `compliance/service_request?request_type=${service}`;
  }

  /** @method get */
  static CASE_SERVICE_REQUEST(caseId: number, service: string) {
    return `case/${caseId}/service_request?request_type=${service}`;
  }

  /** @method get */
  static ROLE_TEMPLATES(mode?: string) {
    let url = `company/role`;

    if (mode) url += `?mode=${mode}`;

    return url;
  }

  /** @method get */
  static GET_FLOW(id: number) {
    return `compliance/flow/${id}`;
  }

  /** @method get */
  static COMPLIANCE_EDIT_STEP(step_id: number | string, params) {
    const query = this.generateQuery(params as API_Params_Obj);

    const url = `/compliance/steps/${step_id}/edit`;

    return `${url}${query}`;
  }

  /** @method get */
  static COMPLIANCE_EDIT_CHECK(params) {
    const url = `/compliance/edit_check`;

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  static COMPLIANCE_CATEGORY_HISTORICAL(categoryId: number | string) {
    return `/compliance/category/${categoryId}/historical`;
  }

  /** @method get */
  static COMPLIANCE_CATEGORY_HISTORICAL_ADD(categoryId: number | string) {
    return `/compliance/category/${categoryId}/historical/add`;
  }

  /** @method get */
  static ROLE_TEMPLATE(id: number) {
    return `company/role/${id}`;
  }

  /** @method get */
  static CREATE_ROLE_TEMPLATE(fromSL = false) {
    if (fromSL) return "compliance/tasks/create_role?from_sl=true";

    return "compliance/tasks/create_role";
  }

  /** @method get */
  static ROLE_TEMPLATE_PROFILE(id: number | string) {
    return `/company/role/${id}/profile`;
  }

  /** @method get */
  static ROLE_TEMPLATE_EDIT_STEP(roleTemplateId: number | string, stepId: number | string) {
    return `/company/role/${roleTemplateId}/steps/${stepId}/edit`;
  }

  /** @method get */
  static ROLE_TEMPLATE_EDIT_CHECK(roleTemplateId: number | string) {
    return `/company/role/${roleTemplateId}/edit_check`;
  }

  /** @method get */
  static SEARCH_SOC_CODE(query: string, start: number, step: number, order = "asc") {
    return `search/job_titles?q=${query}&m=${order}&offset=${start}&limit=${step}`;
  }

  /** @method get */
  static GET_SOC_ITEM(soc_code: string | number) {
    return `job_description?soc_code=${soc_code}`;
  }

  /** @method get */
  static GET_BAND(params: object) {
    const query = this.generateQuery({ ...params } as API_Params_Obj);

    return `job_description${query}`;
  }

  /** @method get */
  static GET_ANSWER_BLOCK() {
    return `flow/answer_block`;
  }

  /** @method get */
  static GET_CASE_ANSWER_RESULT_BY_ALIAS({ caseId, alias }) {
    return `/case/${caseId}/alias_value?alias=${alias}`;
  }

  /** @method post */
  static readonly ADD_ADDRESS_BRANCH = "/company/branch";

  /** */
  static readonly DELETE_ADDRESS_BRANCH = `/company/address`;

  static readonly ADD_PAYE = "company/paye";

  static ADD_COMPANY_QUESTION_VALUE(userFlowId: string | number) {
    return `company/question_value/${userFlowId}`;
  }

  static ADD_COMPANY_ADDRESS_VALUE() {
    return `company/address`;
  }

  /** @method post */
  static readonly GET_CURRENT_USER = "/current_user";

  /** @method get @method post */
  static readonly MY_ACCOUNT = "/my_account";

  /** @method get @method post */
  static readonly MY_ACCOUNT_PASSWORD = "/my_account/password";

  /** @method get */
  static MY_ACCOUNT_PASSWORD_VALIDATE(code: string, time_stamp: number) {
    return `/my_account/password/validate?code=${code}&time_stamp=${time_stamp}`;
  }

  // TODO: delete on 09.10.2024
  // /** @method post */
  // static TASKS_FINISH_ONBOARD_CANDIDATE(task_id: number | string, case_id: number | string) {
  //   return `/case/${case_id}/tasks/${task_id}/onboard_candidate`;
  // }

  /** @method get */
  static readonly GET_COMPANY = "/company/search";

  /** @method get */
  static VALIDATE_COMPANY_NAME(query: string) {
    return `/validate/company_name?q=${query}`;
  }

  /** @method get @method post */
  static readonly COMPANY_INVITE = "/company/invite";

  /** @method get */
  static COMPANY_CHECK_INVITE(token: string) {
    return `/check_invite?token=${token}`;
  }

  /** @method delete */
  static COMPANY_INVITE_DELETE(invite_id: number) {
    return `/company/invite/${invite_id}`;
  }

  /** @method get */
  static COMPANY_INVITE_LINK(invite_id: number, variant: "resend" | "link") {
    return `/company/invite/${invite_id}/${variant}`;
  }

  /** @method get */
  static COMPANY_TEAMMATES(isAdmin = false) {
    if (isAdmin) return "/company/teammates?admin=true";

    return "/company/teammates";
  }

  /** @method get */
  static COMPANY_TEAMMATES_COMPLIANCE_ADMIN(user_id: number) {
    return `/company/teammates/${user_id}/compliance_admin`;
  }

  /** @method get */
  static COMPANY_TEAMMATES_PERMISSION(user_id: number, member: string) {
    return `/company/teammates/${user_id}/permission?value=${member}`;
  }

  /** @method get */
  static COMPANY_TEAMMATES_DELETE_STATUS(user_id: number) {
    return `/company/teammates/${user_id}/delete/status`;
  }

  /** @method delete */
  static COMPANY_TEAMMATES_DELETE(user_id: number) {
    return `/company/teammates/${user_id}/delete`;
  }

  /** @method get */
  static COMPANY_SPONSORSHIP_CANDIDATES(case_id: number) {
    return `/case/${case_id}/sponsorship_candidate`;
  }

  /** @method пуе */
  static COMPANY_SPONSORSHIP_CANDIDATE(
    case_id: number | string,
    sponsorship_candidate_id: number | string
  ) {
    return `/case/${case_id}/sponsorship_candidate/${sponsorship_candidate_id}`;
  }

  /** @method пуе */
  static COMPANY_SPONSORSHIP_CANDIDATE_INFO(
    case_id: number | string,
    sponsorship_candidate_id: number | string
  ) {
    return `/case/${case_id}/sponsorship_candidate/${sponsorship_candidate_id}/info`;
  }

  /** @method post */
  static COMPANY_ADD_SPONSORSHIP_CANDIDATE(case_id: number) {
    return `/case/${case_id}/sponsorship_candidate`;
  }

  /** @method delete */
  static COMPANY_REMOVE_SPONSORSHIP_CANDIDATE(
    case_id: number | string,
    sponsorship_candidate_id: number
  ) {
    return `/case/${case_id}/sponsorship_candidate/${sponsorship_candidate_id}`;
  }

  /** @method get */
  static readonly HOME_PAGE_ONBOARDING = "/home_page/onboarding";

  /** @method get */
  static readonly HOME_PAGE_COMPLIANCE_STATUS = "/home_page/compliance_status";

  /** @method get */
  static HOME_PAGE_ADDITIONAL_INFO() {
    return `/home_page/additional_info`;
  }

  /** @method get */
  static readonly COMPLIANCE_STATISTIC = "/home_page/compliance_statistic";

  /** @method get */
  static readonly COMPANY_CANDIDATES = "/company/candidates";

  /** @method get */
  static readonly COMPLIANCE_ONBOARDING = "/compliance/onboarding";

  /** @method get */
  static readonly COMPLIANCE_STATUS = "/compliance/compliance_status";

  /** @method get */
  static readonly COMPLIANCE_EMPLOYEE_DASHBOARD = "/compliance/employee/dashboard";

  /** @method get */
  static readonly HOME_PAGE_CANDIDATES = "/home_page/candidates";

  /** @method get */
  static readonly CANDIDATES_ARCHIVE = "/candidate?archive=";

  /** @method get */
  static CASE_ARCHIVE(case_id: number) {
    return `/case/${case_id}/archive`;
  }

  /** @method get */
  static CASE_UNARCHIVE(case_id: number) {
    return `/case/${case_id}/unarchive`;
  }

  /** @method get */
  static EXPERT_CASE_ARCHIVE(case_id: number) {
    return `/expert/case/${case_id}/archive`;
  }

  /** @method get */
  static EXPERT_CASE_UNARCHIVE(case_id: number) {
    return `/expert/case/${case_id}/unarchive`;
  }

  /** @method get */
  static readonly COMPANY_SL = "/company/sl";

  /** @method get */
  static CANDIDATE(params?: object) {
    const url = `/candidate`;

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  static CANDIDATE_USER_SUMMARY(user_id: number) {
    return `/candidate/${user_id}/summary`;
  }

  /** @method get */
  static CANDIDATE_USER_CASES(user_id: number) {
    return `/candidate/${user_id}/cases`;
  }

  /** @method get @method post */
  static readonly CANDIDATE_INVITE = "/employer/candidate-invite";

  /** @method get */
  static GET_COUNTRIES_LIST() {
    return `/countries`;
  }

  /** @method get */
  static CASES_REPORT_REGENERATE(case_id: number, alias: string) {
    return `case/${case_id}/report/regenerate?alias=${alias}`;
  }

  /** @method get @method post */
  static CASES_REPORT_ATTACHEMENT(case_id: number) {
    return `case/${case_id}/report/attachment`;
  }

  /** @method get */
  static CASES_REPORT_ATTACHEMENT_GENERATE(case_id: number) {
    return `case/${case_id}/report/attachment/generate`;
  }

  /** @method get */
  static CASES_REPORT_ATTACHEMENT_TO_BE_SENT(
    case_id: number,
    attachment_id: number,
    to_be_send: boolean
  ) {
    return `case/${case_id}/report/attachment/${attachment_id}/to_be_sent?to_be_send=${to_be_send}`;
  }

  /** @method delete */
  static CASES_REPORT_ATTACHEMENT_DELETE(case_id: number, attachment_id: number) {
    return `/case/${case_id}/report/attachment/${attachment_id}`;
  }

  /** @method get */
  static CASES_REPORT_DRAFT(case_id: number, alias: string) {
    return `case/${case_id}/report/draft?alias=${alias}`;
  }

  /** @method get */
  static EXPERT_EMPLOYEES_FILTERS() {
    return "/expert/employees/filters";
  }

  /** @method get */
  static EXPERT_EMPLOYEES_BY_FILTER() {
    return `/expert/employees/by_filter`;
  }

  /** @method get */
  static EXPERT_COMPANIES_FILTERS() {
    return "/expert/companies/filters";
  }

  /** @method get */
  static EXPERT_COMPANIES_BY_FILTER(params?: object) {
    const url = `/expert/companies/by_filter`;

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  static EXPERT_COMPANIES_HIGHLIGHT(company_id: number) {
    return `expert/companies/${company_id}/highlight`;
  }

  /** @method get */
  static EXPERT_COMPANIES_LEVEL_STATUS(company_id: number, status: number) {
    return `expert/companies/${company_id}/level_1_user?status=${status}`;
  }

  /** @method get */
  static EXPERT_COMPANIES_PROFILE(company_id: string) {
    return `expert/companies/${company_id}/profile`;
  }

  /** @method get */
  static EXPERT_COMPANIES_DOCUMENTS(company_id: number) {
    return `expert/companies/${company_id}/documents`;
  }

  /** @method get */
  static EXPERT_COMPANIES_DOCUMENT(company_id: number, document_id: number | string) {
    return `expert/companies/${company_id}/documents/${document_id}`;
  }

  /** @method get */
  static EXPERT_COMPANIES_SERVICES(company_id: number, params?: object) {
    const url = `expert/companies/${company_id}/services`;

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  static EXPERT_COMPANIES_TASKS(company_id: number) {
    return `expert/companies/${company_id}/tasks`;
  }

  /** @method get */
  static EXPERT_COMPANIES_TASKS_NEW(company_id: number) {
    return `expert/companies/${company_id}/tasks/v2`;
  }

  static EXPERT_FROM_COMPANY_LEVEL_TO_EMPLOYER_TASKS(company_id: number) {
    return `expert/companies/${company_id}/tasks`;
  }

  static EXPERT_FROM_COMPANY_LEVEL_TO_EMPLOYER_GET_DOCUMENT_TEMPLATES_LIST(company_id: number) {
    return `expert/companies/${company_id}/tasks/documents`;
  }

  static EXPERT_FOR_EMPLOYEE_TASKS(company_id: number, user_id: number) {
    return `expert/companies/${company_id}/employees/${user_id}/tasks`;
  }

  static EXPERT_FROM_COMPANY_LEVEL_TO_EMPLOYER_DOCUMENT_TASKS(company_id: number) {
    return `expert/companies/${company_id}/tasks/documents`;
  }

  static EXPERT_FROM_EMPLOYEE_LEVEL_TO_EMPLOYER_DOCUMENT_TASKS(
    company_id: number,
    user_id: number
  ) {
    return `expert/companies/${company_id}/employees/${user_id}/tasks/documents?role=employer`;
  }

  static EXPERT_FROM_EMPLOYEE_LEVEL_TO_EMPLOYEE_DOCUMENT_TASKS(
    company_id: number,
    user_id: number
  ) {
    return `expert/companies${company_id}/employees/${user_id}/tasks/documents?role=employee`;
  }

  static EXPERT_FROM_EMPLOYEE_LEVEL_TO_EMPLOYER_GET_DOCUMENT_TEMPLATES_LIST(
    company_id: number,
    user_id: number
  ) {
    return `expert/companies/${company_id}/employees/${user_id}/tasks/documents?role=employer`;
  }

  static EXPERT_FROM_EMPLOYEE_LEVEL_TO_EMPLOYEE_GET_DOCUMENT_TEMPLATES_LIST(
    company_id: number,
    user_id: number
  ) {
    return `expert/companies/${company_id}/employees/${user_id}/tasks/documents?role=employee`;
  }

  /** @method get */
  static EXPERT_COMPANIES_HISTORICAL_DATA(company_id: number) {
    return `expert/companies/${company_id}/historical_data`;
  }

  /** @method get */
  static EXPERT_COMPANIES_EMPLOYEES(company_id: number) {
    return `expert/companies/${company_id}/employees`;
  }

  /** @method get */
  static EXPERT_COMPANIES_EMPLOYEES_SUMMARY(company_id: number, user_id: number) {
    return `expert/companies/${company_id}/employees/${user_id}/summary`;
  }

  /** @method get */
  static EXPERT_COMPANIES_PROFILE_EMPLOYEE(company_id: number, id: number) {
    return `expert/companies/${company_id}/employees/${id}`;
  }

  /** @method get */
  static EXPERT_COMPANIES_EMPLOYEES_HISTORY(company_id: number, user_id: number) {
    return `expert/companies/${company_id}/employees/${user_id}/history`;
  }

  /** @method get */
  static EXPERT_COMPANIES_EMPLOYEES_DOCUMENTS(company_id: number, user_id: number) {
    return `expert/companies/${company_id}/employees/${user_id}/documents`;
  }

  /** @method get */
  static EXPERT_COMPANIES_EMPLOYEES_DOCUMENT(
    company_id: number,
    user_id: number,
    document_id: number | string
  ) {
    return `expert/companies/${company_id}/employees/${user_id}/documents/${document_id}`;
  }

  /** @method get */
  static EXPERT_COMPANIES_EMPLOYEE_NAME_BY_ID(company_id: number, user_id: number) {
    return `expert/companies/${company_id}/employees/${user_id}/name`;
  }

  /** @method get */
  static EXPERT_COMPANIES_NAME(company_id: number) {
    return `expert/companies/${company_id}/name`;
  }

  /** @method get */
  static GET_DEPENDANT() {
    return "/dependant";
  }

  /** @method post */
  static START_DEPENDANT_VISA(caseId: number, dependantId: number) {
    return `/dependant/case?case_id=${caseId}&dependant_id=${dependantId}`;
  }

  /** @method get */
  static readonly EXPERT_INVITES_FILTERS = "/expert/invite/filter";

  /** @method get */
  static EXPERT_INVITES(params?: object) {
    const url = `/expert/invite`;

    const query = this.generateQuery(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method put */
  static EXPERT_UPDATE_INVITE(invite_id: number) {
    return `expert/invite/${invite_id}`;
  }

  /** @method get */
  static EXPERT_GET_INVITE_LINK(invite_id: number) {
    return `expert/invite/${invite_id}/link`;
  }

  /** @method get */
  static EXPERT_RESEND_INVITE(invite_id: number) {
    return `expert/invite/${invite_id}/resend`;
  }

  /** @method get @method put */
  static EXPERT_COMPANY_SUBSCRIPTION_STATUS(company_id: number) {
    return `expert/companies/${company_id}/subscription_details`;
  }

  /** @method get @method put */
  static EXPERT_COMPANY_PREPAID_SERVICES(company_id: number) {
    return `expert/companies/${company_id}/prepaid_services`;
  }

  /** @method get */
  static readonly NOTIFICATIONS_STATUS = "/notifications/status";

  /** @method get */
  static readonly NOTIFICATIONS_SHORT_LIST = "/notifications/short_list";

  /** @method get */
  static NOTIFICATIONS_LIST(params?: object) {
    const url = `/notifications/list`;

    const query = this.generateQueryWithEmptyValues(params as API_Params_Obj);

    return `${url}${query}`;
  }

  /** @method get */
  static NEWS_ITEM(news_id: number) {
    return `/news/${news_id}`;
  }

  /** @method post */
  static readonly SUBMIT_SERVICE_REQUEST = "/service_request";

  // Extension
  /** @method get */
  static readonly COMPANY_EMPLOYEES_EXTENSION = "/company/employees/extension";

  /** @method get */
  static readonly CHATWOOT_CONTACT = "/chatwoot/contact";

  /** @method post */
  static readonly CHATWOOT_CONVERSATION = "/chatwoot/conversation";

  /** @method get */
  static CREATE_EXTENSION_CASE(params?: object) {
    const url = `/case/create`;

    const query = this.generateQueryWithEmptyValues(params as API_Params_Obj);

    return `${url}${query}&is_extension=`;
  }

  /** @method post */
  static readonly CHAT = "/api/gigagent/chat";
}
